import React, { useCallback, useEffect, useState } from 'react';
import EditorMenuControls from './EditorMenuControls';
import {
  LinkBubbleMenu,
  RichTextEditorProvider,
  RichTextField,
} from 'mui-tiptap';
import { useEditor } from '@tiptap/react';
import '../../../styles/mfsStyles.scss';
import useExtensions from './useExtensions';
import ModalFileManager from '../ModalFileManager';
import { uploadType } from '../../../Constants';
import { Upload } from '../../../@types/api';

interface Props {
  content: string;
  setEditor: (editor: any) => void;
  setTouched: () => void;
  isSatellite: boolean;
  isBlog?: boolean;
}

const MfsRichTextEditor: React.FC<Props> = ({
  content,
  setEditor,
  setTouched,
  isSatellite,
  isBlog,
}) => {
  const [show, setShow] = useState<boolean>(false);
  const extensions = useExtensions();
  const editor = useEditor({
    extensions: extensions,
    content: content,
    onUpdate: setTouched,
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }
    if (!editor.isFocused || !editor.isEditable) {
      queueMicrotask(() => {
        const currentSelection = editor.state.selection;
        editor
          .chain()
          .setContent(content)
          .setTextSelection(currentSelection)
          .run();
      });
    }
  }, [content, editor]);

  useEffect(() => {
    setEditor(editor);
  }, [editor, setEditor]);

  const setLink = useCallback(() => {
    const previousUrl = editor?.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      editor?.chain().focus().extendMarkRange('link').unsetLink().run();

      return;
    }

    // update link
    editor
      ?.chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url })
      .run();
  }, [editor]);

  const addImage = useCallback(
    (file: Upload) => {
      if (file) {
        editor
          ?.chain()
          .focus()
          .setImage({
            src: `https://mfsresources.blob.core.windows.net/blog/${file.fileName}`,
          })
          .run();
        setShow(false);
      }
    },
    [editor]
  );

  return (
    <div id={isSatellite ? 'SateliteStyles' : 'MfsStyles'}>
      <RichTextEditorProvider editor={editor}>
        <RichTextField
          variant='outlined'
          controls={
            <EditorMenuControls
              onImageClick={isBlog ? () => setShow(true) : null}
              setLink={setLink}
            />
          }
        />
        <LinkBubbleMenu />
      </RichTextEditorProvider>
      <ModalFileManager
        show={show}
        handleClose={() => setShow(false)}
        fileType={uploadType.blog}
        handleUploadSelect={addImage}
      />
    </div>
  );
};

export default MfsRichTextEditor;
